<template>
	<div class="title-detail-hero" v-bind="{ 'data-jwtv-title': isJustWatchTV || undefined }">
		<div class="title-detail-hero__content">
			<div class="title-detail-hero__details">
				<h1 class="title-detail-hero__details__title" :class="{ 'has-original-title': showOriginalTitle }">
					<template v-if="seasonName">
						<TitleDetailLink :fullPath="titleFullPath" :title="title">
							<span @click.prevent="handleClickTitleDetailLink">{{ titleName }}</span>
						</TitleDetailLink>
						-
						{{ seasonName }}
					</template>
					<template v-else>
						{{ titleName }}
					</template>

					<span v-if="!!releaseYear" class="release-year"> ({{ releaseYear }})</span>
				</h1>
				<h3 v-if="showOriginalTitle" class="original-title">
					{{ $t('WEBAPP_ORIGINAL_TITLE') }}: {{ originalTitle }}
				</h3>
				<TitleDetailInfoRow :title="title" :showChartInfo="showChartInfo" />

				<div class="info-container">
					<slot></slot>
				</div>
			</div>

			<div
				class="title-detail-hero__backdrop"
				:class="{ 'title-detail-hero__backdrop--empty': isBackdropEmpty }"
				@click="handleBackdropClick"
				v-observe-visibility="observeVisibilityOptions"
			>
				<template v-if="mountImpressionPixel">
					<ImpressionPixel v-for="tag in impressionPixelTags" :key="tag" :impressionTag="tag" />
				</template>
				<div class="title-detail-hero__backdrop-container">
					<ProviderPromotionOffer
						class="title-detail-hero__backdrop__provider-promo"
						v-if="titleHasBackdropPromo"
						:additionalContexts="additionalContexts"
						:title="title"
					/>
					<JustWatchTVPlayBtn v-else-if="isJustWatchTV" :disable="hasModalOpened">
						<div>
							<div v-show="!isMounted || hasModalOpened" class="lds-ring sm">
								<div></div>
								<div></div>
								<div></div>
								<div></div>
							</div>
							<FontAwesomeIcon v-show="isMounted && !hasModalOpened" :icon="faPlay" size="2x" />
						</div>
					</JustWatchTVPlayBtn>
					<div
						class="title-detail-hero__backdrop__play"
						:class="{
							'title-detail-hero__backdrop__play--loading': !isMounted,
							'title-detail-hero__backdrop__play--active': hasModalOpened,
						}"
						v-else-if="!isLoggedIn && titleClips.length > 0"
					>
						<div class="title-detail-hero__backdrop__play__container">
							<div v-show="!isMounted || hasModalOpened" class="lds-ring">
								<div></div>
								<div></div>
								<div></div>
								<div></div>
							</div>
							<FontAwesomeIcon v-show="isMounted && !hasModalOpened" :icon="faPlay" size="2x" />
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="title-detail-hero__backdrop_background">
			<PictureComp
				v-if="imageUrls.length > 0"
				imageProfile="backdrop"
				:imageUrl="imageUrls[0]"
				:alt="titleName"
				:lazy="false"
				fetchpriority="high"
				:object-position-top="true"
			/>
			<img v-else src="/appassets/img/backdrop-placeholder.jpg" class="placeholder__backdrop" />
			<div class="backdrop-gradient"></div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useExperiment } from '@/libraries/experiment';

import ProviderPromotionOffer from '@/components/ProviderPromotionOffer.vue';
import TitleDetailInfoRow from '@/pages/titleDetailV1/components/titleDetailInfoRow.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import TrailerModal from '@/components/TrailerModal.vue';
import { SnowplowLinkContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { TitleDetail } from '@/interfaces/title-details-graphql';
import { getBackdropClips, getSelfHostedTrailerClips } from '@/helpers/title-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { AdPlacement, ClipProvider, MonetizationType, SraPlacement } from '@/@types/graphql-types';
import { SnowplowTitleContextGraphql, type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { OfferLocation } from '@/helpers/offer-helper';
import { getTrailerComponent, isBitMovinCountry } from '@/helpers/trailer-helper';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { afterNextPaint } from '@/helpers/async-helper';

// SEASON_PAGE_BUYBOX_EXP
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { getDisneyImpressionUrl, useProviderPromotion } from '@/helpers/provider-promotion';
import { getHboImpressionUrl } from '@/helpers/impression-tracker-helper';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { useGetSrPlacement } from '@/features/sponsored-recommendations/composables/useGetSrPlacement';
import adminConfig from '@/features/promotions/config.json';
// SEASON_PAGE_BUYBOX_EXP

import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';

import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { generateUUIDv4 } from '@/helpers/uuid-helper';
import { VideoPlayersTrackingIds } from '@/constants/video-players-tracking-ids';
import ImpressionPixel from '@/features/sponsored-recommendations/components/ImpressionPixel.vue';
const JustWatchTVPlayBtn = defineAsyncComponent(() => import('./JustwatchTVPlayBtn.vue'));

const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		originalTitle?: string;
		seasonName?: string | null;
		AVODOffer?: TitleOfferFragment | null;
		activeClipId?: string | null;
		isModal?: boolean;
		additionalContexts: SnowplowContext[];
		showChartInfo: boolean;
	}>(),
	{
		additionalContexts: () => [],
		activeClipId: null,
		seasonName: null,
	}
);

const emits = defineEmits(['setActiveClipId', 'closeTitleDetailModal']);

const { country, webLocale } = storeToRefs(useLanguageStore());
const { activeRoute } = storeToRefs(useRoutingStore());

const { xServeTrack } = storeToRefs(useAppStore());
const { isLoggedIn } = storeToRefs(useUserStore());

const { activeVariantWithControlGroup: tvodCinematicVariant, trackEvent: trackCinematicEvent } =
	useExperiment('EXP_JWTV_TVOD_CINEMATIC_V2');

useProvideClickoutPlacement('backdrop');

const isTrailerModalQtvActive = computed(() => {
	if (
		!adminConfig?.steering_overrides_us?.trailer_modal_qtv ||
		!adminConfig.steering_overrides_us.trailer_modal_provider ||
		country.value !== 'US'
	)
		return false;

	return props.title.offers.some(
		offer => offer.package.shortName === adminConfig.steering_overrides_us.trailer_modal_provider
	);
});

// We wanna hide the trailer ads when there is a survey campaign linking to our title page.
const hideTrailerAd = computed(() => {
	return activeRoute.value?.query['utm_campaign']?.includes('SRV');
});

const isJustWatchTV = computed(
	() =>
		props.title.justwatchTVOffers.length > 0 &&
		props.title.justwatchTVOffers[0].monetizationType === MonetizationType.Free &&
		props.title.justwatchTVOffers[0].package.technicalName === 'justwatchtv'
);

// ** SRs Adserver Migration ** //
const backdropVideoSrConfig = adminConfig?.ad_server?.srs?.placements?.backdrop_video_sr;
const useAdserverBackdropSrMutation = computed(
	() => backdropVideoSrConfig?.all_countries || backdropVideoSrConfig?.countries?.includes(country.value)
);

const { sponsoredAd } = useAdserverBackdropSrMutation.value
	? useGetSrPlacement({
			pageType: 'VIEW_TITLE_DETAIL',
			placement: SraPlacement.BackdropVideo,
			adPlacement: AdPlacement.BackdropVideoSr,
	  })
	: useSendBidId({
			pageType: 'VIEW_TITLE_DETAIL',
			placement: SraPlacement.BackdropVideo,
	  });

// **** SRs Backdrop Experiment ****
const hasUserClickedOnTrailerBackdrop = ref(false);
// **** SRs Backdrop Experiment ****

const titleClips = computed(() => {
	const PlexClip = props.AVODOffer
		? [
				{
					provider: 'PLEX',
					externalId: props.AVODOffer.id,
					sourceUrl: props.AVODOffer.standardWebURL,
				},
		  ]
		: null;

	const OttOffer = props.title.offers.find(
		offer => offer.package.technicalName === 'ottstudioplayer' && !!offer.streamUrl
	);
	const OttClip = OttOffer
		? [
				{
					provider: 'OTT',
					externalId: OttOffer.id,
					sourceUrl: OttOffer.streamUrl,
				},
		  ]
		: null;

	const JustWatchTVClip = isJustWatchTV.value
		? [
				{
					provider: 'JUSTWATCH_TV',
					externalId: props.title.justwatchTVOffers[0].id,
					sourceUrl: isBitMovinCountry(country.value)
						? props.title.justwatchTVOffers[0].streamUrlExternalPlayer ??
						  props.title.justwatchTVOffers[0].streamUrl
						: props.title.justwatchTVOffers[0].streamUrl,
				},
		  ]
		: null;

	// Self-hosted JWTV trailers take priority — they work for all users (no login required)
	// and play through BitmovinPlayer instead of a third-party iframe.
	const selfHostedBackdropClip = getSelfHostedTrailerClips(props.title);
	return JustWatchTVClip || selfHostedBackdropClip || PlexClip || OttClip || getBackdropClips(props.title);
});

const { titleHasBackdropPromo } = useProviderPromotion(() => props.title);

const titleName = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.title : props.title.content.title;
});

const titleFullPath = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.fullPath : props.title.content.fullPath;
});

const releaseYear = computed(() => {
	return props.title.content.originalReleaseYear;
});

const showTitleId = computed(() =>
	props.title.__typename === 'Season' && !!props.title.show ? props.title.show.id : props.title.id
);

const showOriginalTitle = computed(() => !!props.originalTitle && props.originalTitle !== props.title.content.title);

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

// remove the empty backdrop space, if in the backdrop there is
// - no backdrop
// - no play trailer button
// - no play JWTV button
// - no provider promotion button
const isBackdropEmpty = computed(
	() =>
		props.title.content.backdrops.length === 0 && // no backdrop images
		!titleHasBackdropPromo.value && // no provider "play button in backdrop" promotion
		!isJustWatchTV.value && // no JWTV
		titleClips.value.length === 0 // no trailers
);

const isMounted = ref(false);
const hasModalOpened = ref(false);
// we need an additional state that isn't tied to re-renderings, but prevents users from opening multiple modal windows.
const isModalOpening = ref(false);

const imageUrls = computed(() => props.title.content.backdrops.map(backdrop => backdrop.backdropUrl));

const mountImpressionPixel = ref(false);

const impressionPixelTags = computed(() => {
	const tags: string[] = [];
	const disneyUrl = props.title.disneyOffersCount > 0 ? getDisneyImpressionUrl('dnp', webLocale.value) : null;
	if (disneyUrl) tags.push(disneyUrl);
	const hboUrl = props.title.offers.some(o => o.package.shortName === 'mxx')
		? getHboImpressionUrl(country.value)
		: null;
	if (hboUrl) tags.push(hboUrl);
	return tags;
});

const hasImpressionPixels = computed(() => impressionPixelTags.value.length > 0);

const offerForImpression = computed(() => {
	if (isJustWatchTV.value) return props.title.justwatchTVOffers[0];
	if (props.AVODOffer) return props.AVODOffer;

	return null;
});

const observeVisibilityOptions = computed(() => {
	return !!offerForImpression.value || hasImpressionPixels.value
		? {
				callback: onVisibilityChanged,
				...observableOptions,
		  }
		: null;
});

// create clickoutUrl for offerImpression tracking. Can pass the created contexts to the track event.
const clickoutUrl = computed(() => {
	if (!offerForImpression.value || titleHasBackdropPromo.value) return null;

	const objectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const objectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;

	return new ClickoutUrl(offerForImpression.value)
		.setTitleSourceId(props.title?.id ?? null)
		.addContexts(new SnowplowTitleContextGraphql(objectId, objectType))
		.addContexts(props.additionalContexts)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.set('uct_country', country.value)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'video_player')
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('TitleContextGraphql', 'jwRawEntityId', props.title?.id ?? null)
		.setGenre(props.title?.content.genres)
		.setCanary(xServeTrack.value);
});

function onVisibilityChanged(isVisible: boolean) {
	if (!!clickoutUrl.value) onIntersection(clickoutUrl.value, isVisible);

	if (hasImpressionPixels.value && !mountImpressionPixel.value && isVisible) {
		mountImpressionPixel.value = true;
	}
}

async function handleBackdropClick() {
	if (isLoggedIn.value && !isJustWatchTV.value) {
		return;
	}

	// IPN case: trailer modal is one of the heaviest UI pieces we have.
	// that's why we split it into 2 tasks upon interaction:
	// 1) show spinner
	// 2) show modal
	hasModalOpened.value = true;
	await afterNextPaint();

	if (clickoutUrl.value) {
		// TODO: This is not a "clickout" it should be replace with a more relevant event
		trackClickout(clickoutUrl.value);
	}

	openTrailerModal(false);
	// for SR Backdrop Experiment
	hasUserClickedOnTrailerBackdrop.value = true;
}

async function openTrailerModal(nonInteraction: boolean) {
	if (titleClips.value.length === 0 || titleHasBackdropPromo.value) return;
	if (isModalOpening.value) return;
	isModalOpening.value = true;

	try {
		hasModalOpened.value = true; // important to have this after the setTimeout, so the re-rendering is happening afterwards
		const impressionUUID = generateUUIDv4();
		const LinkContext = new SnowplowLinkContext({
			self: impressionUUID,
		});
		TrackingHelper.trackEvent(
			'youtube_started',
			{
				action: props.title.objectType.toLowerCase(),
				label: 'backdrop',
				value: VideoPlayersTrackingIds[titleClips.value[0].provider as ClipProvider],
				nonInteraction,
			},
			[LinkContext]
		);

		emits('setActiveClipId', titleClips.value[0].externalId);
		// wait for modal to be closed again

		await ModalHelper.openModal(
			TrailerModal,
			{
				clips: titleClips.value,
				title: props.title,
				source: 'backdrop',
				additionalContexts: props.additionalContexts,
				playerAdditionalContexts:
					titleClips.value[0].provider === ClipProvider.Justwatchtv
						? [LinkContext]
						: getClickoutUrlContextsArray(clickoutUrl.value),
				sponsoredAd: sponsoredAd.value,
				sraPlacement: SraPlacement.BackdropVideo,
				hideTrailerAd: hideTrailerAd.value,
			},
			{ cssClass: 'trailer-modal', lockScroll: !isTrailerModalQtvActive.value },
			'',
			true,
			[],
			() => (hasModalOpened.value = false)
		);
	} catch (e) {
		console.error(e);
	} finally {
		isModalOpening.value = false;
		hasModalOpened.value = false;
	}
}

function handleClickTitleDetailLink() {
	// navigation is to same url, so the modal is not destroyed
	if (props.isModal && titleFullPath.value) {
		emits('closeTitleDetailModal');
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: showTitleId.value,
			property: 'title-header',
		},
		props.additionalContexts
	);
}

watch(
	tvodCinematicVariant,
	variant => {
		if (variant === 'control') {
			trackCinematicEvent({ action: 'impression' });
		}
	},
	{ immediate: true }
);

onMounted(() => {
	if (props.activeClipId) {
		emits('setActiveClipId', null);
	}
	isMounted.value = true;
	TrackingHelper.trackEvent('clip_spinner', {
		action: 'finished',
		value: 1,
		property: titleClips.value.length > 0 ? titleClips.value[0].provider : 'none',
		nonInteraction: true,
	});

	// INP: preload the trailer component's JS already for slow devices
	getTrailerComponent(titleClips.value[0]?.provider as ClipProvider);

	setTimeout(() => {
		const query = activeRoute.value?.query!;
		if (query['trailer'] === 'play') {
			openTrailerModal(true);
		}
	});
});

watch(activeRoute, () => {
	emits('setActiveClipId', null);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins';
@import '~@/styles/mixins.scss';

$backdrop-max-width: $app-max-width - ($app-x-margin * 2) - 100px;

.title-detail-hero {
	position: relative;
	width: 100%;
	display: flex;
	overflow: hidden;
	.title-detail-hero-details {
		@include sm() {
			margin-bottom: 24px;
		}
	}
	.info-container {
		@media screen and (min-width: 600px) and (max-width: 1050px) {
			margin-top: 36px;
		}
		@media (min-width: 1050px) {
			margin-top: auto;
		}
	}

	&__content {
		width: 100%;
		max-width: 1440px;
		display: flex;
		margin-inline: auto;
		@include sm() {
			flex-direction: column-reverse;
		}
	}

	&__backdrop_background {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 0;
		display: flex;
		flex-direction: row-reverse;
		background-color: #0c131e;
		.picture-comp__img {
			margin-inline-start: auto;
		}
		.picture-comp {
			width: 50%;
		}
		img {
			height: 100%;
			max-height: 300px;
			width: 50%;
			&.placeholder__backdrop {
				object-fit: cover;
				@include sm() {
					max-height: 120px;
					width: 100%;
				}
			}
		}
		@include sm() {
			:deep(img) {
				max-height: 120px;
			}
			:deep(.picture-comp) {
				width: 100%;
			}
		}
		.backdrop-gradient {
			position: absolute;
			top: 0;
			inset-inline-start: 50%;
			width: 50%;
			height: 100%;
			background: linear-gradient(90deg, #0c131e 0%, rgba(12, 19, 30, 0) 100%);
			@include rtl {
				background: linear-gradient(-90deg, #0c131e 0%, rgba(12, 19, 30, 0) 100%);
			}
			z-index: 2;
			@include sm() {
				max-height: 120px;
				width: 100%;
				inset-inline-start: 0;
			}
		}
	}

	&__details {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		padding-inline: 64px 0;
		padding-block: 32px;
		width: 50%;
		position: relative;
		z-index: 2;
		@include sm() {
			width: 100%;
		}
		@media (max-width: 1050px) {
			justify-content: space-evenly;
			padding: 16px 16px;
		}
		@media (max-width: 600px) {
			justify-content: flex-start;
			// padding: 0px 16px;
		}

		// > * {
		// 	white-space: nowrap;
		// }

		&__title {
			-webkit-font-smoothing: antialiased;
			font-family: Anton, Anton-Fallback, Impact;
			font-size: 32px;
			font-weight: 400;
			text-transform: uppercase;
			margin-top: 0;

			&.has-original-title {
				margin-bottom: 16px;
				@media (max-width: 600px) {
					margin-bottom: 8px;
				}
			}

			.release-year {
				font-size: 22px;
				color: #6f7d90;
			}
		}
		.original-title {
			margin-top: 0px;
			font-weight: 400;
			line-height: 16px;
			font-size: 16px;
			line-height: 24px;
			margin-bottom: 16px;

			@media (max-width: 600px) {
				margin-bottom: 8px;
				color: #727f92;
			}
		}
	}

	&__backdrop {
		position: relative;
		width: 50%;
		z-index: 1;
		min-height: 250px;
		@include sm() {
			width: 100%;
			min-height: 120px;
		}

		&:hover {
			cursor: pointer;
		}

		&--empty {
			margin-top: -120px;
		}

		&-container {
			position: absolute;
			height: 100%;
			top: 0;
			inset-inline-start: 0;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		&__play {
			border-radius: 500px;
			height: 64px;
			width: 64px;
			display: flex;
			justify-content: center;
			align-items: center;
			background-color: #fbc500;
			color: #000000;

			z-index: 3;

			@media (min-width: 1050px) {
				margin-bottom: 28px;
			}

			@media (max-width: 1050px) {
				margin-bottom: 12px;
			}

			@include sm() {
				margin-inline-start: 16px;
				margin-bottom: 0px;
			}

			&--loading {
				pointer-events: none;
			}

			&--active {
				pointer-events: none;
			}

			&__container {
				position: relative;
			}
		}

		&__provider-promo {
			@include sm() {
				margin-inline-start: 16px;
			}
		}
	}
}
.jwt-play {
	@media (min-width: 1050px) {
		margin-bottom: 50px;
	}

	@media (max-width: 1050px) {
		margin-bottom: 28px;
	}
	@include sm() {
		margin-bottom: 0px;
	}
}

.backdrops {
	width: 100%;
	display: flex;
}

$size: 32px;
$border: 3px;

.lds-ring {
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	pointer-events: none;
	&.sm {
		height: unset;
		width: unset;
	}
}
.lds-ring div {
	box-sizing: border-box;
	display: block;
	position: absolute;
	width: $size;
	height: $size;
	margin: $border;
	border: $border solid #fff;
	border-radius: 50%;
	animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
	border-color: #fff transparent transparent transparent;
}
.lds-ring div:nth-child(1) {
	animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
	animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
	animation-delay: -0.15s;
}
@keyframes lds-ring {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}
</style>
